// src/services/blogService.js - Complete with SEO Support
import { getCurrentUserToken } from './firebase';

const API_BASE_URL = '/api';
const BASE_URL = `${API_BASE_URL}/blogs`;

// Helper function to handle API responses
const handleResponse = async (
  response,
  showSuccessToast = false,
  successMessage = '',
) => {
  let data;
  try {
    data = await response.json();
  } catch (error) {
    // If response is not JSON, get text
    const text = await response.text();
    throw new Error(text || 'Invalid server response', { cause: error });
  }

  // Check for success flag in response
  if (!response.ok || (data && data.success === false)) {
    const errorMessage = data?.message || data?.error || 'Request failed';
    console.error('API Error:', errorMessage);
    throw new Error(errorMessage);
  }

  if (showSuccessToast && successMessage) {
    console.log('Success:', successMessage);
  }

  return data;
};

// Helper to get auth headers
const getAuthHeaders = async () => {
  try {
    const token = await getCurrentUserToken();
    if (!token) {
      return {};
    }
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    return {};
  }
};

// Helper to get form data headers
const getFormDataHeaders = async () => {
  try {
    const token = await getCurrentUserToken();
    if (!token) {
      return {};
    }
    return {
      Authorization: `Bearer ${token}`,
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    return {};
  }
};

// =============================================
// PUBLIC METHODS
// =============================================

export const getBlogs = async (options = {}) => {
  const {
    page = 1,
    limit = 12,
    category,
    tag,
    search,
    sortBy = 'publishedAt',
    sortOrder = 'desc',
  } = options;

  const params = new URLSearchParams({
    action: 'list',
    page: page.toString(),
    limit: limit.toString(),
    sortBy,
    sortOrder,
    ...(category && { category }),
    ...(tag && { tag }),
    ...(search && { search }),
  });

  const response = await fetch(`${BASE_URL}?${params}`);
  return handleResponse(response);
};

export const getBlogBySlug = async slug => {
  const response = await fetch(`${BASE_URL}?action=single&slug=${slug}`);
  return handleResponse(response);
};

export const getCategories = async () => {
  const response = await fetch(`${BASE_URL}?action=categories`);
  return handleResponse(response);
};

export const getTags = async () => {
  const response = await fetch(`${BASE_URL}?action=tags`);
  return handleResponse(response);
};

export const getRelatedPosts = async (id, category, tags, slug, limit = 3) => {
  const params = new URLSearchParams({
    action: 'related',
    id,
    category,
    slug,
    limit: limit.toString(),
    ...(tags && { tags: tags.join(',') }),
  });
  const response = await fetch(`${BASE_URL}?${params}`);
  return handleResponse(response);
};

// =============================================
// SEO METHODS
// =============================================

/**
 * Get SEO score for a blog post
 */
export const getSEOScore = async blogId => {
  const response = await fetch(`${BASE_URL}?action=seo-score&id=${blogId}`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Auto-generate SEO fields for a blog post
 */
export const autoGenerateSEO = async blogId => {
  const response = await fetch(
    `${BASE_URL}?action=auto-generate-seo&id=${blogId}`,
    {
      method: 'POST',
      headers: await getAuthHeaders(),
    },
  );
  return handleResponse(
    response,
    true,
    'SEO fields auto-generated successfully',
  );
};

/**
 * Bulk update SEO for multiple blogs
 */
export const bulkUpdateSEO = async (ids, seoData) => {
  const response = await fetch(`${BASE_URL}?action=bulk-update-seo`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ ids, seoData }),
  });
  return handleResponse(
    response,
    true,
    `SEO updated for ${ids.length} blog(s)`,
  );
};

// =============================================
// COMMENT METHODS
// =============================================

export const getComments = async (blogId, limit = 50) => {
  const response = await fetch(
    `${BASE_URL}?action=get-comments&blogId=${blogId}&limit=${limit}`,
  );
  return handleResponse(response);
};

export const addComment = async commentData => {
  const response = await fetch(`${BASE_URL}?action=add-comment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(commentData),
  });
  return handleResponse(response, true, 'Comment added successfully');
};

export const likeComment = async commentId => {
  const response = await fetch(`${BASE_URL}?action=like-comment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ commentId }),
  });
  return handleResponse(response);
};

// =============================================
// ADMIN COMMENT METHODS
// =============================================

export const updateCommentSettings = async (blogId, settings) => {
  const response = await fetch(`${BASE_URL}?action=update-comment-settings`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ blogId, ...settings }),
  });
  return handleResponse(response, true, 'Settings updated successfully');
};

export const getAdminComments = async blogId => {
  const response = await fetch(
    `${BASE_URL}?action=admin-get-comments&blogId=${blogId}`,
    {
      headers: await getAuthHeaders(),
    },
  );
  return handleResponse(response);
};

export const adminUpdateComment = async (commentId, content) => {
  const response = await fetch(`${BASE_URL}?action=admin-update-comment`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ commentId, content }),
  });
  return handleResponse(response, true, 'Comment updated successfully');
};

export const adminDeleteComment = async commentId => {
  const response = await fetch(`${BASE_URL}?action=admin-delete-comment`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ commentId }),
  });
  return handleResponse(response, true, 'Comment deleted successfully');
};

// =============================================
// ADMIN BLOG METHODS
// =============================================

export const getAdminBlogs = async (options = {}) => {
  const {
    page = 1,
    limit = 50,
    category,
    tag,
    status,
    search,
    sortBy = 'createdAt',
    sortOrder = 'desc',
    featured,
  } = options;

  const params = new URLSearchParams({
    action: 'admin-list',
    page: page.toString(),
    limit: limit.toString(),
    sortBy,
    sortOrder,
    ...(category && { category }),
    ...(tag && { tag }),
    ...(status && { status }),
    ...(search && { search }),
    ...(featured !== undefined && { featured: featured.toString() }),
  });

  const response = await fetch(`${BASE_URL}?${params}`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse(response);
};

export const getBlogById = async id => {
  const response = await fetch(`${BASE_URL}?action=single-admin&id=${id}`, {
    headers: await getAuthHeaders(),
  });
  return handleResponse(response);
};

// src/services/blogService.js - Add debug logs

export const createBlog = async formData => {
  console.log('📤 createBlog called with FormData');
  try {
    const response = await fetch(`${BASE_URL}?action=create`, {
      method: 'POST',
      headers: await getFormDataHeaders(),
      body: formData,
    });

    console.log('📥 createBlog response status:', response.status);
    const data = await response.json();
    console.log('📥 createBlog response data:', data);

    if (!response.ok || data.success === false) {
      throw new Error(data.message || 'Failed to create blog');
    }

    return data;
  } catch (error) {
    console.error('❌ createBlog error:', error);
    throw error;
  }
};

export const updateBlog = async (id, formData) => {
  console.log(`📤 updateBlog called for ${id} with FormData`);
  try {
    const response = await fetch(`${BASE_URL}?action=update&id=${id}`, {
      method: 'POST',
      headers: await getFormDataHeaders(),
      body: formData,
    });

    console.log('📥 updateBlog response status:', response.status);
    const data = await response.json();
    console.log('📥 updateBlog response data:', data);

    if (!response.ok || data.success === false) {
      throw new Error(data.message || 'Failed to update blog');
    }

    return data;
  } catch (error) {
    console.error('❌ updateBlog error:', error);
    throw error;
  }
};

export const deleteBlog = async id => {
  const response = await fetch(`${BASE_URL}?action=delete&id=${id}`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });
  return handleResponse(response, true, 'Blog deleted successfully');
};

export const deleteTempImage = async publicId => {
  const response = await fetch(
    `${BASE_URL}?action=delete-temp-image&publicId=${encodeURIComponent(publicId)}`,
    {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    },
  );
  return handleResponse(response, true, 'Temporary image deleted successfully');
};

export const bulkDeleteBlogs = async ids => {
  const response = await fetch(`${BASE_URL}?action=bulk-delete`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ ids }),
  });
  return handleResponse(
    response,
    true,
    `${ids.length} blog(s) deleted successfully`,
  );
};

export const bulkUpdateStatus = async (ids, status) => {
  const response = await fetch(`${BASE_URL}?action=bulk-update-status`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ ids, status }),
  });
  return handleResponse(
    response,
    true,
    `${ids.length} blog(s) updated to ${status}`,
  );
};

export const toggleFeatured = async (id, featured) => {
  const response = await fetch(`${BASE_URL}?action=toggle-featured`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ id, featured }),
  });
  return handleResponse(
    response,
    true,
    `Blog ${featured ? 'featured' : 'unfeatured'} successfully`,
  );
};

export const uploadImage = async file => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(`${BASE_URL}?action=upload-image`, {
    method: 'POST',
    headers: await getFormDataHeaders(),
    body: formData,
  });
  return handleResponse(response);
};

export const deleteImage = async publicId => {
  const response = await fetch(`${BASE_URL}?action=delete-image`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ publicId }),
  });
  return handleResponse(response);
};

/**
 * Update reading time for a blog post
 * Adds the time spent to the existing reading time
 */
export const updateReadingTime = async (blogId, timeSpentSeconds) => {
  try {
    const response = await fetch(`${BASE_URL}?action=update-reading-time`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        blogId,
        timeSpent: timeSpentSeconds,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update reading time');
    }

    return data;
  } catch (error) {
    console.error('Failed to update reading time:', error);
    throw error;
  }
};

/**
 * Get reading time analytics for a blog
 */
export const getReadingTimeAnalytics = async blogId => {
  const response = await fetch(
    `${BASE_URL}?action=reading-time-analytics&id=${blogId}`,
    {
      headers: await getAuthHeaders(),
    },
  );
  return handleResponse(response);
};

// =============================================
// ANALYTICS METHODS
// =============================================

export const getBlogAnalytics = async (period = '30days') => {
  const response = await fetch(
    `${BASE_URL}?action=analytics-overview&period=${period}`,
    {
      headers: await getAuthHeaders(),
    },
  );
  return handleResponse(response);
};

export const getBlogDetailAnalytics = async blogId => {
  const response = await fetch(
    `${BASE_URL}?action=analytics-blog-detail&blogId=${blogId}`,
    {
      headers: await getAuthHeaders(),
    },
  );
  return handleResponse(response);
};

export const getCommentAnalytics = async (
  period = '30days',
  status = 'all',
) => {
  const response = await fetch(
    `${BASE_URL}?action=analytics-comments&period=${period}&status=${status}`,
    {
      headers: await getAuthHeaders(),
    },
  );
  return handleResponse(response);
};

// Export all methods as a service object
export const blogService = {
  // Public
  getBlogs,
  getBlogBySlug,
  getCategories,
  getTags,
  getRelatedPosts,
  updateReadingTime,
  getReadingTimeAnalytics,

  // SEO
  getSEOScore,
  autoGenerateSEO,
  bulkUpdateSEO,

  // Admin
  getAdminBlogs,
  getBlogById,
  createBlog,
  updateBlog,
  deleteBlog,
  deleteTempImage,
  bulkDeleteBlogs,
  bulkUpdateStatus,
  toggleFeatured,
  uploadImage,
  deleteImage,

  // Comments
  getComments,
  addComment,
  likeComment,
  updateCommentSettings,
  getAdminComments,
  adminUpdateComment,
  adminDeleteComment,

  // Analytics
  getBlogAnalytics,
  getBlogDetailAnalytics,
  getCommentAnalytics,
};
